import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { IncidentService } from '../../../../core/services/incident.service';
import { ModalComponent } from '../../../../shared/components/modal/modal';
import { Person, personTagline } from '../../../../shared/models/person.models';
import { ToastService } from '../../../../core/services/toast.service';
import { apiErrorMessage } from '../../../../shared/utils/api-error';

@Component({
  selector: 'app-add-party-modal',
  imports: [ModalComponent, NgClass],
  templateUrl: './add-party-modal.html',
})
export class AddPartyModalComponent implements OnInit {
  personTypeCode = input<string | undefined>(undefined);
  excludePersonTypeCode = input<string | undefined>(undefined);

  personSelected = output<Person>();
  closed = output<void>();

  private incidentService = inject(IncidentService);
  private destroyRef = inject(DestroyRef);
  private toast = inject(ToastService);

  protected activeTab = signal<'select' | 'create'>('select');

  // ── Select tab ────────────────────────────────────────────────────────────

  protected persons = signal<Person[]>([]);
  protected readonly tagline = personTagline;
  protected personsLoading = signal(false);
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  protected filteredPersons = computed(() => this.persons());

  // ── Create tab ────────────────────────────────────────────────────────────

  protected firstName = signal('');
  protected surnames = signal('');
  protected creating = signal(false);
  protected showErrors = signal(false);

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadPersons();
  }

  private loadPersons(): void {
    this.personsLoading.set(true);
    this.incidentService
      .getPersons(undefined, 50, this.personTypeCode(), this.excludePersonTypeCode())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.persons.set(res.persons);
          this.personsLoading.set(false);
        },
        error: () => this.personsLoading.set(false),
      });
  }

  protected onSearch(term: string): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.personsLoading.set(true);
      this.incidentService
        .getPersons(term || undefined, 50, this.personTypeCode(), this.excludePersonTypeCode())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            this.persons.set(res.persons);
            this.personsLoading.set(false);
          },
          error: () => this.personsLoading.set(false),
        });
    }, 300);
  }

  protected selectPerson(person: Person): void {
    this.personSelected.emit(person);
    this.closed.emit();
  }

  // ── Create ────────────────────────────────────────────────────────────────

  protected onSubmitCreate(): void {
    if (!this.firstName().trim() || !this.surnames().trim()) {
      this.showErrors.set(true);
      return;
    }
    this.creating.set(true);
    this.incidentService
      .createPerson({ firstName: this.firstName().trim(), surnames: this.surnames().trim() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (person) => {
          this.personSelected.emit(person);
          this.closed.emit();
        },
        error: (err) => {
          this.creating.set(false);
          this.toast.show(apiErrorMessage(err, 'Failed to create person'), 'error');
        },
      });
  }
}
